import { useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { useNavigate } from "react-router";
import { useGoBack } from "../../shared/phone/history";
import { useNative } from "../../shared/phone/media";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { api, ApiError } from "../lib/api";
import { productsApi } from "../lib/productsApi";
import { Icon } from "../components/ui/Icon";
import { FlowHead, StepLine } from "./products/FlowHead";
import { useToast } from "./products/useToast";
import "../styles/new-product.css";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** The keyboard's return key steps to the next field, as a form on the phone does; on the last it closes. */
const next = (to: RefObject<HTMLInputElement | null> | null) => (e: KeyboardEvent<HTMLInputElement>) => {
  if (e.key !== "Enter") return;
  e.preventDefault();
  if (to?.current) to.current.focus();
  else e.currentTarget.blur();
};

/** Add a product (`/products/new`), the first step: whose business it is. Then the agent learns it. */
export function NewProduct() {
  const navigate = useNavigate();
  const { toast, toastNode } = useToast();
  const [name, setName] = useState("");
  const [place, setPlace] = useState("");
  const [website, setWebsite] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const placeRef = useRef<HTMLInputElement>(null);
  const webRef = useRef<HTMLInputElement>(null);
  const native = useNative();
  const goBack = useGoBack();

  const learn = async () => {
    if (busy) return;
    if (!name.trim()) {
      toast("Write the business name first.", false);
      nameRef.current?.focus();
      return;
    }
    setBusy(true);
    let id: string;
    try {
      // Why: the place is short and public, so it is the product's line; the owner's notes go to the agent only.
      const { product } = await api.products.create({ name: name.trim(), description: place.trim() || null, website: website.trim() || null });
      id = product.id;
    } catch (e) {
      toast(message(e), false);
      setBusy(false);
      return;
    }
    const note = [place.trim() && `Where we work: ${place.trim()}`, notes.trim()].filter(Boolean).join("\n\n");
    const to = `/products/${encodeURIComponent(id)}/profile`;
    try {
      await productsApi.learn(id, note ? { note } : {});
      navigate(to, { replace: native });
    } catch (e) {
      navigate(to, { replace: native, state: { error: message(e) } });
    }
  };

  if (native) {
    return (
      <div className="pn-screen">
        <PhoneNav
          title="Whose business is it?"
          large
          back={{ to: "/products", label: "Products" }}
          left={
            <button type="button" className="pn-tbtn" onClick={() => goBack("/products")}>
              Cancel
            </button>
          }
          sub={<StepLine at={1} />}
        />
        <div className="pn-stack tight">
          <div className="pn-grp">
            <div className="pn-irow col">
              <label htmlFor="np-name">Business name</label>
              <input ref={nameRef} id="np-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Required" autoComplete="off" enterKeyHint="next" onKeyDown={next(placeRef)} />
            </div>
            <div className="pn-irow col">
              <label htmlFor="np-place">Where they work</label>
              <input
                ref={placeRef}
                id="np-place"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                placeholder="A city, a region or a country"
                autoComplete="off"
                enterKeyHint="next"
                onKeyDown={next(webRef)}
              />
            </div>
            <div className="pn-irow col">
              <label htmlFor="np-web">Website</label>
              <input
                ref={webRef}
                id="np-web"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="Optional"
                inputMode="url"
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                enterKeyHint="done"
                onKeyDown={next(null)}
              />
            </div>
          </div>
          <p className="pn-note">A name and a place are enough. A website makes it faster.</p>
          <div className="pn-grp">
            <div className="pn-irow col">
              <label htmlFor="np-notes">Anything you already have</label>
              <textarea
                id="np-notes"
                rows={3}
                placeholder="Optional. Notes, an FAQ or a pitch. Your words outrank public pages."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="pn-foot">
          <button type="button" className="pn-btn pri wide" onClick={learn} disabled={busy}>
            Learn the business
            <Icon name="right" />
          </button>
        </div>
        {toastNode}
      </div>
    );
  }

  return (
    <section className="wrap ga addprod">
      <FlowHead stage="Who" title="Whose business is it?" sub="A name and a place are enough. A website makes it faster." />
      <section className="card">
        <label className="fl" style={{ marginTop: 0 }} htmlFor="lsN">
          Business name
        </label>
        <input ref={nameRef} className="field" id="lsN" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoFocus />
        <div className="row2">
          <div>
            <label className="fl" htmlFor="lsP">
              Where they work
            </label>
            <input className="field" id="lsP" value={place} onChange={(e) => setPlace(e.target.value)} autoComplete="off" />
          </div>
          <div>
            <label className="fl" htmlFor="lsW">
              Website
            </label>
            <input
              className="field"
              id="lsW"
              placeholder="Optional"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              autoComplete="off"
              inputMode="url"
            />
          </div>
        </div>
        <label className="fl" htmlFor="lsM">
          Anything you already have
        </label>
        <textarea
          className="field"
          id="lsM"
          rows={3}
          placeholder="Optional. Notes, an FAQ or a pitch. Your words outrank public pages."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </section>
      <div className="wz-nav">
        <span className="sp" />
        <button type="button" className="btn" onClick={learn} disabled={busy}>
          Learn the business
          <Icon name="right" />
        </button>
      </div>
      {toastNode}
    </section>
  );
}
